/**
 * @name PasswordForm
 * @description Componente común de formulario y validación para crear y cambiar contraseñas
 * @author Ricardo Guerra
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { Icons, Layout, MUI } from '@styles';

import PasswordCheck from './components/password-check';

class PasswordForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: props.user,
      changePassword: props.changePassword,
      retrievePassword: props.retrievePassword,
      lengthV: false,
      numbersV: false,
      lettersV: false,
      capitalLettersV: false,
      symbolsV: false
    };

    this.values = {
      isChangePassword: false,
      newPassword: '',
      oldPassword: '',
      confirmPassword: ''
    };

    this.schema = Yup.object({
      newPassword: Yup.string()
        .min(8, i18n.t('FormsGeneralTexts.min8Chars'))
        .max(20, i18n.t('FormsGeneralTexts.max20Chars'))
        .matches(/(?=.*\d)/, i18n.t('FormsGeneralTexts.oneDigit'))
        .matches(/(?=.*[a-z])/, i18n.t('FormsGeneralTexts.oneLowerLetter'))
        .matches(/(?=.*[A-Z])/, i18n.t('FormsGeneralTexts.oneCapitalLetter'))
        .matches(/[-$#%^&*()_+|~=`{}[\]:";'<>¨?¡¿!°¬@,./]/, i18n.t('FormsGeneralTexts.oneSymbol'))
        .required('*'),
      oldPassword: Yup.string()
        .when('isChangePassword', {
          is: true,
          then: Yup.string().required('*'),
          otherwise: Yup.string().notRequired()
        }),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('newPassword'), null], i18n.t('ChangePass.noPassMatch'))
        .required('*'),
      isChangePassword: Yup.boolean()
    });
  }

  /**
   * Validación de contraseña (lenght, lowerletters, capitalletters, numbers, symbols)
   * @param password newPassword
   */
  validatePassword = (password) => {
    let numbers = '0123456789',
      letters = 'abcdefghiyjklmnñopqrstuvwxyz',
      capitalLetters = 'ABCDEFGHIYJKLMNÑOPQRSTUVWXYZ',
      symbols = '.;,:-_+*¨´\'?¡¿!#$%&/()=\\~^`}{|°¬@}';

    let lengthV = false,
      numbersV = false,
      lettersV = false,
      capitalLettersV = false,
      symbolsV = false;

    // Length
    if (password.length >= 8) {
      lengthV = true;
    }

    for (let i = 0; i < password.length; i++) {
      // Numbers
      for (let j = 0; j < numbers.length; j++) {
        if (numbersV === false) {
          if (password.charAt(i) === numbers.charAt(j)) {
            numbersV = true;
          }
        }
      }
      // Letters
      for (let j = 0; j < letters.length; j++) {
        if (lettersV === false) {
          if (password.charAt(i) === letters.charAt(j)) {
            lettersV = true;
          }
        }
      }
      // Capital Letters
      for (let j = 0; j < capitalLetters.length; j++) {
        if (capitalLettersV === false) {
          if (password.charAt(i) === capitalLetters.charAt(j)) {
            capitalLettersV = true;
          }
        }
      }
      // Symbols
      for (let j = 0; j < symbols.length; j++) {
        if (symbolsV === false) {
          if (password.charAt(i) === symbols.charAt(j)) {
            symbolsV = true;
          }
        }
      }
    }

    this.setState({
      lengthV,
      numbersV,
      lettersV,
      capitalLettersV,
      symbolsV
    });
  }

  resetPasswordCheck = () => {
    this.setState({
      lengthV: false,
      numbersV: false,
      lettersV: false,
      capitalLettersV: false,
      symbolsV: false
    });
  }

  render() {
    const {
      user,
      changePassword,
      retrievePassword,
      lengthV,
      numbersV,
      lettersV,
      capitalLettersV,
      symbolsV
    } = this.state;

    return (
      <MUI.Grid container>
        <MUI.Grid item md={8} sm={6} xs={12}>
          <MUI.Box padding="16px">
            <PasswordCheck
              isChangePassword={changePassword}
              lengthV={lengthV}
              lettersV={lettersV}
              capitalLettersV={capitalLettersV}
              numbersV={numbersV}
              symbolsV={symbolsV}
            />
          </MUI.Box>
        </MUI.Grid>

        <MUI.Grid item md={4} sm={6} xs={12}>
          <MUI.Box padding="16px">
            <Formik
              validateOnMount
              initialValues={this.values}
              initialErrors={this.values}
              initialTouched={this.values}
              validationSchema={this.schema}
              onSubmit={(values, { setSubmitting, resetForm }) => {
                this.props.onConfigPassword(values, setSubmitting, resetForm, this.resetPasswordCheck);
              }}
            >
              {(formikProps) => {
                formikProps.values.isChangePassword = (changePassword && !retrievePassword);
                return (
                  <MUI.Box
                    display="flex"
                    flexDirection="column"
                    width="100%"
                  >
                    {/* User Input */}
                    {!changePassword && (
                      <MUI.Box padding="8px">
                        <MUI.TextFieldIcon
                          label={i18n.t('GenericMessages.user')}
                          value={user}
                          disabled={true}
                          InputProps={{ startAdornment: (<Icons.Person />) }}
                        />
                      </MUI.Box>
                    )}

                    {/* Old Password Input */}
                    {(changePassword && !retrievePassword) && (
                      <MUI.Box padding="8px">
                        <MUI.TextFieldIcon
                          label={i18n.t('ChangePass.previousPass')}
                          type="password"
                          name="oldPassword"
                          value={formikProps.values.oldPassword}
                          onBlur={formikProps.handleBlur}
                          onChange={formikProps.handleChange}
                          onInput={() => formikProps.setFieldTouched('oldPassword', true, true)}
                          error={Boolean(formikProps.touched.oldPassword && formikProps.errors.oldPassword)}
                          helperText={formikProps.touched.oldPassword && formikProps.errors.oldPassword}
                          InputProps={{ startAdornment: (<Icons.Lock />) }}
                        />
                      </MUI.Box>
                    )}

                    {/* New Password Input */}
                    <MUI.Box padding="8px">
                      <MUI.TextFieldIcon
                        label={i18n.t('ChangePass.newPass')}
                        type="password"
                        name="newPassword"
                        value={formikProps.values.newPassword}
                        onBlur={formikProps.handleBlur}
                        onChange={formikProps.handleChange}
                        onInput={() => formikProps.setFieldTouched('newPassword', true, true)}
                        onKeyUp={() => this.validatePassword(formikProps.values.newPassword)}
                        error={Boolean(formikProps.touched.newPassword && formikProps.errors.newPassword)}
                        helperText={formikProps.touched.newPassword && formikProps.errors.newPassword}
                        InputProps={{ startAdornment: (<Icons.Lock />) }}
                      />
                    </MUI.Box>

                    {/* Confirm Password Input */}
                    <MUI.Box padding="8px">
                      <MUI.TextFieldIcon
                        label={i18n.t('ChangePass.confirmPass')}
                        type="password"
                        name="confirmPassword"
                        value={formikProps.values.confirmPassword}
                        onBlur={formikProps.handleBlur}
                        onChange={formikProps.handleChange}
                        onInput={() => formikProps.setFieldTouched('confirmPassword', true, true)}
                        error={Boolean(formikProps.touched.confirmPassword && formikProps.errors.confirmPassword)}
                        helperText={formikProps.touched.confirmPassword && formikProps.errors.confirmPassword}
                        InputProps={{ startAdornment: (<Icons.Lock />) }}
                      />
                    </MUI.Box>
                    <MUI.Box
                      display="flex"
                      alignItems="center"
                      paddingTop="8px"
                    >
                      <Layout.BoxRequired>
                        {`* ${i18n.t('FormsGeneralTexts.required')}`}
                      </Layout.BoxRequired>
                      <MUI.Box display="flex" justifyContent="right">
                        <MUI.ButtonCTA
                          type="button"
                          color="primary"
                          disabled={!formikProps.isValid || formikProps.isSubmitting}
                          onClick={formikProps.handleSubmit}
                        >
                          {changePassword ? i18n.t('DialogConfirm.save') : i18n.t('DialogConfirm.continue')}
                        </MUI.ButtonCTA>
                      </MUI.Box>
                    </MUI.Box>
                  </MUI.Box>
                );
              }}
            </Formik>
          </MUI.Box>
        </MUI.Grid>
      </MUI.Grid>
    );
  }
}

PasswordForm.propTypes = {
  // El userName del usuario.
  user: PropTypes.string,
  // Boolean que indica si se está cambiando la contraseña.
  changePassword: PropTypes.bool.isRequired,
  // Boolean que indica si se está recuperando la contraseña.
  retrievePassword: PropTypes.bool.isRequired,
  // Función para configurar la contraseña.
  onConfigPassword: PropTypes.func,
};

export default PasswordForm;